"use client";

import React from "react";
import { Upload, Check, Loader, Paperclip } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function Candidatura() {
  const {
    ref
  } = useRevealOnScroll();
  return <section id="candidatura" ref={ref} className="relative w-full py-20 overflow-hidden text-white transition-opacity duration-700 opacity-100 !bg-[hsl(238,100%,85%)]">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 flex flex-col gap-8 order-2 lg:order-1">
            <div className="relative h-[600px] w-full rounded-sm overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-black/20 z-10 mix-blend-multiply" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559159/img/modern_law_office_architecture.png" alt="Ufficio legale moderno con architettura in vetro e acciaio" className="h-full w-full object-cover grayscale-[0.3] contrast-125" />
              <div className="absolute bottom-0 left-0 w-full p-8 bg-gradient-to-t from-black/90 to-transparent z-20">
                <p className="text-white/80 text-sm leading-relaxed">
                  Unisciti a un team che costruisce il futuro della professione legale con precisione, struttura e visione.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="mb-8">
              <h2 className="text-3xl text-white mb-4 !font-sans md:text-4xl">Candidatura</h2>
              <p className="text-white/60">
                Compila il modulo sottostante per candidarti alle posizioni aperte. Siamo sempre alla ricerca di nuovi talenti.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-md border border-white/10 p-8 md:p-10 rounded-sm shadow-xl">
              <FormProvider formId="candidatura_form_lavoraconnoi" sectionName="candidatura" defaultValues={{
              full_name: "",
              email: "",
              resume: "",
              cover_letter: "",
              privacy_consent: false
            }} validationRules={{
              full_name: {
                required: "Il nome è obbligatorio"
              },
              email: {
                required: "L'email è obbligatoria",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Inserisci un'email valida"
                }
              },
              resume: {
                required: "Il CV è obbligatorio"
              },
              cover_letter: {
                required: "La lettera di presentazione è obbligatoria"
              },
              privacy_consent: {
                required: "L'accettazione della privacy è obbligatoria"
              }
            }} submitText="Invia Candidatura" successMessage="Grazie! La tua candidatura è stata ricevuta. Il nostro team la valuterà con attenzione e ti ricontatterà al più presto." errorMessage="Si è verificato un errore durante l'invio. Per favore, riprova più tardi.">
                <CandidaturaForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function CandidaturaForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 rounded-full bg-[hsl(36,33%,69%)]/20 flex items-center justify-center mb-6">
          <Check className="w-8 h-8 text-[hsl(36,33%,69%)]" />
        </div>
        <h3 className="text-2xl font-sans text-white mb-2">Candidatura Inviata</h3>
        <p className="text-white/70 max-w-md">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="full_name" rules={{
        required: "Il nome è obbligatorio"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/80 font-medium text-sm">Nome e Cognome</FormLabel>
              <FormControl>
                <Input placeholder="Mario Rossi" {...field} className="border-0 border-b border-white/20 rounded-none bg-transparent px-0 py-2 h-auto text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-[hsl(36,33%,69%)] transition-colors duration-300" />
              </FormControl>
              <FormDescription className="text-white/40 text-xs">
                Inserisci il tuo nome completo.
              </FormDescription>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "L'email è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Inserisci un'email valida"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/80 font-medium text-sm">Indirizzo Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="mario.rossi@example.com" {...field} className="border-0 border-b border-white/20 rounded-none bg-transparent px-0 py-2 h-auto text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-[hsl(36,33%,69%)] transition-colors duration-300" />
              </FormControl>
              <FormDescription className="text-white/40 text-xs">
                Utilizzeremo questa email per contattarti.
              </FormDescription>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      <FormField control={control} name="resume" rules={{
      required: "Il CV è obbligatorio"
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="space-y-2">
            <FormLabel className="text-white/80 font-medium text-sm">Curriculum Vitae (CV)</FormLabel>
            <FormControl>
              <div className="relative group">
                <Input type="file" accept=".pdf,.docx,.doc" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={e => {
            const file = e.target.files?.[0];
            if (file) onChange(file.name);
          }} {...field} value={undefined} />
                <div className="border-2 border-dashed border-white/20 rounded-sm p-6 flex flex-col items-center justify-center text-center transition-all duration-300 group-hover:border-[hsl(36,33%,69%)] group-hover:bg-white/5">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mb-3 group-hover:bg-[hsl(36,33%,69%)]/20 transition-colors">
                    {value ? <Paperclip className="w-5 h-5 text-[hsl(36,33%,69%)]" /> : <Upload className="w-5 h-5 text-white/60 group-hover:text-[hsl(36,33%,69%)]" />}
                  </div>
                  <p className="text-sm text-white/80 font-medium">
                    {value ? <span className="text-[hsl(36,33%,69%)]">{value}</span> : "Carica il tuo CV"}
                  </p>
                  <p className="text-xs text-white/40 mt-1">PDF o DOCX (Max 5MB)</p>
                </div>
              </div>
            </FormControl>
            <FormDescription className="text-white/40 text-xs">
              Carica il tuo CV in formato PDF o DOCX.
            </FormDescription>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      <FormField control={control} name="cover_letter" rules={{
      required: "La lettera di presentazione è obbligatoria"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-white/80 font-medium text-sm">Lettera di Presentazione</FormLabel>
            <FormControl>
              <Textarea placeholder="Raccontaci perché sei il candidato ideale per questa posizione..." {...field} className="min-h-[120px] border-0 border-b border-white/20 rounded-none bg-transparent px-0 py-2 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-[hsl(36,33%,69%)] resize-none transition-colors duration-300" />
            </FormControl>
            <FormDescription className="text-white/40 text-xs">
              Facoltativo, ma consigliato per aumentare le tue possibilità.
            </FormDescription>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      <FormField control={control} name="privacy_consent" rules={{
      required: "L'accettazione della privacy è obbligatoria"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 py-2">
            <div className="pt-1">
              <FormControl>
                <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} className="h-4 w-4 rounded border-white/20 bg-white/10 text-[hsl(36,33%,69%)] focus:ring-[hsl(36,33%,69%)] focus:ring-offset-0 cursor-pointer accent-[hsl(36,33%,69%)]" />
              </FormControl>
            </div>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-white/80 text-sm font-normal cursor-pointer">
                Dichiaro di aver preso visione de{" "}
                <Link to="https://studiodclegal.it/informativa-candidati/" newTab className="underline hover:text-[hsl(36,33%,69%)] transition-colors">
                  l&apos;Informativa Candidati
                </Link>
                {" "}resa ai sensi dell&apos;art. 13 del GDPR
              </FormLabel>
              <FormMessage className="text-red-400" />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-sm">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-[hsl(36,33%,69%)] hover:bg-[hsl(36,33%,69%)]/90 text-[hsl(217,33%,17%)] font-semibold rounded-[4px] h-12 px-8 uppercase tracking-wide transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-4 w-4" />
            Invio in corso...
          </> : "Invia Candidatura"}
      </Button>
    </Form>;
}